import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function mbToGiB(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return (n / 1024).toFixed(1);
}

function getRegion(row) {
  for (const k of ['Region', 'env_name', 'region']) {
    const val = row[k];
    if (val != null && String(val).trim() !== '') return String(val).trim();
  }
  return '';
}

function getDisplayName(row) {
  return (
    (row['Friendly name'] && String(row['Friendly name']).trim()) ||
    (row['Instance Name'] && String(row['Instance Name']).trim()) ||
    (row['instance'] && String(row['instance']).trim()) ||
    ''
  );
}

function getInstanceUrl(row) {
  const domain = String(row['Instance Domain'] ?? '').trim();
  const name = String(row['Instance Name'] ?? '').trim();
  if (!domain || !name) return null;
  const base = domain.startsWith('http') ? domain : `https://${domain}`;
  return `${base.replace(/\/$/, '')}/${name.replace(/^\//, '')}`;
}

function formatMemory(raw) {
  const gib = mbToGiB(raw);
  if (gib != null) return `${gib} GiB`;
  if (raw && String(raw).trim()) return String(raw).trim();
  return '—';
}

function getAppServer(row) {
  return String(row['App server'] ?? '').trim();
}

function getCategory(row) {
  return String(row['Customer Category'] ?? '').trim();
}

function getVersion(row) {
  for (const k of ['Release', 'current_version', 'Current Version', 'Version']) {
    const val = row[k];
    if (val != null && String(val).trim() !== '') return String(val).trim();
  }
  return '';
}

/**
 * RAG status based on category tier and number of clients sharing the same app server.
 *   VIP1   : >25 → red, >15 → amber, else green
 *   VIP2   : >25 → red, >20 → amber, else green
 *   Standard: >35 → red, else green (no amber threshold)
 *   UAT / unset: no status
 */
function getPeerStatus(category, peerCount) {
  if (category === 'VIP1') {
    if (peerCount > 25) return 'red';
    if (peerCount > 15) return 'amber';
    return 'green';
  }
  if (category === 'VIP2') {
    if (peerCount > 25) return 'red';
    if (peerCount > 20) return 'amber';
    return 'green';
  }
  if (category === 'Standard') {
    if (peerCount > 35) return 'red';
    return 'green';
  }
  return null;
}

const STATUS_RANK = { red: 3, amber: 2, green: 1 };

function statusTooltip(category, peerCount) {
  if (category === 'VIP1')
    return `${peerCount} clients on this server · VIP1 thresholds: >15 amber, >25 red`;
  if (category === 'VIP2')
    return `${peerCount} clients on this server · VIP2 thresholds: >20 amber, >25 red`;
  if (category === 'Standard')
    return `${peerCount} clients on this server · Standard threshold: >35 red`;
  return '';
}

const STATUS_CHIP_PROPS = {
  green: { label: 'Green', color: 'success' },
  amber: { label: 'Amber', color: 'warning' },
  red:   { label: 'Red',   color: 'error'   }
};

function StatusChip({ status, tooltip }) {
  if (!status) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const { label, color } = STATUS_CHIP_PROPS[status];
  const chip = <Chip label={label} color={color} size="small" />;
  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
}

const CATEGORY_CHIP_PROPS = {
  VIP1:     { color: 'error',   label: 'VIP1' },
  VIP2:     { color: 'warning', label: 'VIP2' },
  Standard: { color: 'info',    label: 'Standard' },
  UAT:      { color: 'success', label: 'UAT' }
};

const CATEGORY_OPTIONS = ['VIP1', 'VIP2', 'Standard', 'UAT'];
const STATUS_OPTIONS = ['green', 'amber', 'red'];
const PAGE_SIZE = 100;

// ─── Column sort configuration ────────────────────────────────────────────────

const COLUMNS = [
  {
    id: 'status',
    label: 'Status',
    sortKey: (row, peerMap) => {
      const appServer = getAppServer(row);
      const count = appServer ? (peerMap.get(appServer) ?? []).length : 0;
      const s = getPeerStatus(getCategory(row), count);
      return STATUS_RANK[s] ?? 0;
    }
  },
  { id: 'instance',  label: 'Instance',            sortKey: (row) => getDisplayName(row).toLowerCase() },
  { id: 'region',    label: 'Region',               sortKey: (row) => getRegion(row).toLowerCase() },
  { id: 'category',  label: 'Category',             sortKey: (row) => getCategory(row).toLowerCase() },
  { id: 'memory',    label: 'Max Memory',           sortKey: (row) => Number(row['Max Memory'] ?? 0) || 0 },
  { id: 'threads',   label: 'Max Threads',          sortKey: (row) => Number(row['Max Threads'] ?? 0) || 0 },
  { id: 'dbconn',    label: 'Max DB Connections',   sortKey: (row) => Number(row['Max DB Connections'] ?? 0) || 0 },
  {
    id: 'peers',
    label: 'App Server Peers',
    sortKey: (row, peerMap) => {
      const appServer = getAppServer(row);
      return appServer ? (peerMap.get(appServer) ?? []).length : 0;
    }
  }
];

// ─── CSV export ───────────────────────────────────────────────────────────────

function escapeCsv(val) {
  const s = String(val ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function exportToCsv(rows, appServerPeers) {
  const headers = [
    'Status', 'Instance', 'Instance URL', 'Region', 'Category',
    'Max Memory', 'Max Threads', 'Max DB Connections',
    'App Server', 'Same Machine Client Count', 'Same Machine Clients'
  ];

  const dataRows = rows.map((row) => {
    const cat = getCategory(row);
    const appServer = getAppServer(row);
    const peers = appServer ? (appServerPeers.get(appServer) ?? []) : [];
    const peerCount = peers.length;
    const status = getPeerStatus(cat, peerCount) ?? '';
    const peerNames = peers.map(getDisplayName).filter(Boolean).join('; ');

    return [
      status,
      getDisplayName(row),
      getInstanceUrl(row) ?? '',
      getRegion(row),
      cat,
      formatMemory(String(row['Max Memory'] ?? '').trim()),
      String(row['Max Threads'] ?? '').trim(),
      String(row['Max DB Connections'] ?? '').trim(),
      appServer,
      peerCount,
      peerNames
    ].map(escapeCsv).join(',');
  });

  const csv = [headers.map(escapeCsv).join(','), ...dataRows].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `infrastructure-resource-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * @param {{ rows: object[], onBulkCategoryUpdate?: (ids: string[], category: string) => Promise<void> }} props
 */
export default function InfrastructureResourceTab({ rows = [], onBulkCategoryUpdate }) {
  const [regionFilter, setRegionFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('');
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(new Set()); // Set of _id strings
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const regions = useMemo(
    () => [...new Set(rows.map(getRegion).filter(Boolean))].sort(),
    [rows]
  );

  const appServerPeers = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const srv = getAppServer(row);
      if (!srv) return;
      if (!map.has(srv)) map.set(srv, []);
      map.get(srv).push(row);
    });
    return map;
  }, [rows]);

  // RAG counts per category across the full dataset (ignores active filters)
  const ragSummary = useMemo(() => {
    const cats = ['VIP1', 'VIP2', 'Standard'];
    const result = {};
    cats.forEach((cat) => { result[cat] = { green: 0, amber: 0, red: 0, total: 0 }; });
    rows.forEach((row) => {
      const cat = getCategory(row);
      if (!result[cat]) return;
      const appServer = getAppServer(row);
      const count = appServer ? (appServerPeers.get(appServer) ?? []).length : 0;
      const status = getPeerStatus(cat, count);
      if (status) result[cat][status]++;
      result[cat].total++;
    });
    return result;
  }, [rows, appServerPeers]);

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      if (regionFilter !== 'all' && getRegion(row) !== regionFilter) return false;
      if (categoryFilter !== 'all' && getCategory(row) !== categoryFilter) return false;
      if (clientFilter) {
        const q = clientFilter.toLowerCase();
        if (!getDisplayName(row).toLowerCase().includes(q)) return false;
      }
      if (statusFilter !== 'all') {
        const appServer = getAppServer(row);
        const peers = appServer ? (appServerPeers.get(appServer) ?? []) : [];
        const status = getPeerStatus(getCategory(row), peers.length);
        if (status !== statusFilter) return false;
      }
      return true;
    });
  }, [rows, regionFilter, categoryFilter, statusFilter, clientFilter, appServerPeers]);

  const sorted = useMemo(() => {
    if (!sortBy) return filtered;
    const col = COLUMNS.find((c) => c.id === sortBy);
    if (!col) return filtered;
    return [...filtered].sort((a, b) => {
      const va = col.sortKey(a, appServerPeers);
      const vb = col.sortKey(b, appServerPeers);
      if (va < vb) return sortOrder === 'asc' ? -1 : 1;
      if (va > vb) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortBy, sortOrder, appServerPeers]);

  const handleSort = useCallback((colId) => {
    setSortBy((prev) => {
      if (prev === colId) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortOrder('asc');
      return colId;
    });
  }, []);

  useEffect(() => {
    setPage(0);
    setSelected(new Set());
  }, [regionFilter, categoryFilter, statusFilter, clientFilter, sortBy, sortOrder, rows]);

  const filteredIds = useMemo(() => new Set(filtered.map((r) => r._id).filter(Boolean)), [filtered]);
  const allFilteredSelected = filteredIds.size > 0 && [...filteredIds].every((id) => selected.has(id));
  const someFilteredSelected = [...filteredIds].some((id) => selected.has(id)) && !allFilteredSelected;

  const handleSelectAll = useCallback(() => {
    setSelected((prev) => {
      if (allFilteredSelected) {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      filteredIds.forEach((id) => next.add(id));
      return next;
    });
  }, [allFilteredSelected, filteredIds]);

  const handleToggleRow = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleToggleExpand = useCallback((key) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleBulkApply = useCallback(async () => {
    if (!bulkCategory || selected.size === 0 || !onBulkCategoryUpdate) return;
    setBulkSaving(true);
    try {
      await onBulkCategoryUpdate([...selected], bulkCategory);
      setSelected(new Set());
      setBulkCategory('');
    } finally {
      setBulkSaving(false);
    }
  }, [bulkCategory, selected, onBulkCategoryUpdate]);

  const paginated = useMemo(
    () => sorted.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [sorted, page]
  );

  return (
    <Box sx={{ p: 0 }}>
      {/* RAG Summary */}
      <Paper variant="outlined" sx={{ mb: 2, px: 2, py: 1.25, borderRadius: 2 }}>
        <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={2}>
          {['VIP1', 'VIP2', 'Standard'].map((cat) => {
            const s = ragSummary[cat];
            return (
              <Box key={cat} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                  {cat}
                </Typography>
                <Tooltip title={`${s.total} total ${cat} instance${s.total !== 1 ? 's' : ''}`}>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {s.total} total
                  </Typography>
                </Tooltip>
                <Chip
                  label={`● ${s.green}`}
                  size="small"
                  color="success"
                  variant={s.green > 0 ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, cursor: 'default' }}
                />
                <Chip
                  label={`● ${s.amber}`}
                  size="small"
                  color="warning"
                  variant={s.amber > 0 ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, cursor: 'default' }}
                />
                <Chip
                  label={`● ${s.red}`}
                  size="small"
                  color="error"
                  variant={s.red > 0 ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, cursor: 'default' }}
                />
              </Box>
            );
          })}
        </Stack>
      </Paper>

      {/* Filters + Export */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        {regions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Region</InputLabel>
            <Select value={regionFilter} label="Region" onChange={(e) => setRegionFilter(e.target.value)}>
              <MenuItem value="all">All regions</MenuItem>
              {regions.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Customer Category</InputLabel>
          <Select value={categoryFilter} label="Customer Category" onChange={(e) => setCategoryFilter(e.target.value)}>
            <MenuItem value="all">All categories</MenuItem>
            {CATEGORY_OPTIONS.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All statuses</MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search client…"
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          sx={{ minWidth: 180 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        <Box sx={{ ml: 'auto' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => exportToCsv(sorted, appServerPeers)}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Bulk action bar */}
      {onBulkCategoryUpdate && selected.size > 0 && (
        <Paper
          variant="outlined"
          sx={{ mb: 1.5, px: 2, py: 1, borderRadius: 2, bgcolor: 'primary.50', borderColor: 'primary.200', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
            {selected.size} instance{selected.size !== 1 ? 's' : ''} selected
          </Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Assign category</InputLabel>
            <Select
              value={bulkCategory}
              label="Assign category"
              disabled={bulkSaving}
              onChange={(e) => setBulkCategory(e.target.value)}
            >
              <MenuItem value=""><em>(None)</em></MenuItem>
              {CATEGORY_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            disabled={!bulkCategory || bulkSaving}
            onClick={handleBulkApply}
            startIcon={bulkSaving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {bulkSaving ? 'Saving…' : 'Apply to selected'}
          </Button>
          <Button
            size="small"
            disabled={bulkSaving}
            onClick={() => { setSelected(new Set()); setBulkCategory(''); }}
          >
            Clear selection
          </Button>
        </Paper>
      )}

      {filtered.length === 0 ? (
        <Alert severity="info">No instances match the selected filters.</Alert>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {onBulkCategoryUpdate && (
                    <TableCell padding="checkbox" sx={{ width: 40 }}>
                      <Checkbox
                        size="small"
                        checked={allFilteredSelected}
                        indeterminate={someFilteredSelected}
                        onChange={handleSelectAll}
                        disabled={bulkSaving}
                      />
                    </TableCell>
                  )}
                  {COLUMNS.map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{ fontWeight: 700 }}
                      sortDirection={sortBy === col.id ? sortOrder : false}
                    >
                      <TableSortLabel
                        active={sortBy === col.id}
                        direction={sortBy === col.id ? sortOrder : 'asc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((row, i) => {
                  const name = getDisplayName(row);
                  const url = getInstanceUrl(row);
                  const region = getRegion(row);
                  const memory = formatMemory(String(row['Max Memory'] ?? '').trim());
                  const threads = String(row['Max Threads'] ?? '').trim() || '—';
                  const dbConn = String(row['Max DB Connections'] ?? '').trim() || '—';
                  const cat = getCategory(row);
                  const chipProps = CATEGORY_CHIP_PROPS[cat];
                  const appServer = getAppServer(row);
                  const peers = appServer ? (appServerPeers.get(appServer) ?? []) : [];
                  const peerCount = peers.length;
                  const status = getPeerStatus(cat, peerCount);
                  const tip = statusTooltip(cat, peerCount);

                  const rowId = row._id;
                  const isSelected = rowId ? selected.has(rowId) : false;
                  const expandKey = rowId || String(i);
                  const isExpanded = expandedRows.has(expandKey);
                  const hasPeers = peerCount > 1;
                  const otherPeers = peers.filter((p) => p !== row);
                  // colSpan covers: (optional checkbox) + 8 COLUMNS
                  const colSpan = (onBulkCategoryUpdate ? 1 : 0) + COLUMNS.length;

                  return (
                    <React.Fragment key={expandKey}>
                      <TableRow
                        selected={isSelected}
                        sx={{ '&:hover': { bgcolor: 'action.hover' }, ...(isSelected && { bgcolor: 'action.selected' }) }}
                      >
                        {onBulkCategoryUpdate && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              disabled={!rowId || bulkSaving}
                              onChange={() => rowId && handleToggleRow(rowId)}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <StatusChip status={status} tooltip={tip} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {hasPeers ? (
                              <Tooltip title={isExpanded ? 'Collapse peers' : `Show ${otherPeers.length} peer${otherPeers.length !== 1 ? 's' : ''} on ${appServer}`}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleExpand(expandKey)}
                                  sx={{ p: 0.25, color: 'text.secondary' }}
                                >
                                  {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Box sx={{ width: 24 }} />
                            )}
                            {url ? (
                              <Typography
                                component="a"
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                fontWeight={600}
                                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                              >
                                {name || '—'}
                              </Typography>
                            ) : (
                              <Typography variant="body2" fontWeight={600}>{name || '—'}</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{region || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          {chipProps ? (
                            <Chip label={chipProps.label} color={chipProps.color} size="small" />
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{memory}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{threads}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{dbConn}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={peerCount > 0 ? 'text.primary' : 'text.secondary'}>
                            {peerCount > 0 ? peerCount : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {hasPeers && (
                        <TableRow sx={{ '& > td': { pb: 0, pt: 0 } }}>
                          <TableCell colSpan={colSpan} sx={{ p: 0, border: isExpanded ? undefined : 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                  App Server: {appServer} — {peerCount} clients on this machine
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                                      <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Instance</TableCell>
                                      <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Version</TableCell>
                                      <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Region</TableCell>
                                      <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Category</TableCell>
                                      <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Max Memory</TableCell>
                                      <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Max Threads</TableCell>
                                      <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Max DB Connections</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {otherPeers.map((peer, pi) => {
                                      const pName = getDisplayName(peer);
                                      const pUrl = getInstanceUrl(peer);
                                      const pRegion = getRegion(peer);
                                      const pCat = getCategory(peer);
                                      const pChip = CATEGORY_CHIP_PROPS[pCat];
                                      const pMemory = formatMemory(String(peer['Max Memory'] ?? '').trim());
                                      const pThreads = String(peer['Max Threads'] ?? '').trim() || '—';
                                      const pDbConn = String(peer['Max DB Connections'] ?? '').trim() || '—';
                                      const pVersion = getVersion(peer);
                                      return (
                                        <TableRow key={pi} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                          <TableCell sx={{ py: 0.5 }}>
                                            {pUrl ? (
                                              <Typography
                                                component="a"
                                                href={pUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                variant="body2"
                                                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                              >
                                                {pName || '—'}
                                              </Typography>
                                            ) : (
                                              <Typography variant="body2">{pName || '—'}</Typography>
                                            )}
                                          </TableCell>
                                          <TableCell sx={{ py: 0.5 }}>
                                            <Typography variant="body2">{pVersion || '—'}</Typography>
                                          </TableCell>
                                          <TableCell sx={{ py: 0.5 }}>
                                            <Typography variant="body2">{pRegion || '—'}</Typography>
                                          </TableCell>
                                          <TableCell sx={{ py: 0.5 }}>
                                            {pChip ? (
                                              <Chip label={pChip.label} color={pChip.color} size="small" />
                                            ) : (
                                              <Typography variant="body2" color="text.secondary">—</Typography>
                                            )}
                                          </TableCell>
                                          <TableCell sx={{ py: 0.5 }}>
                                            <Typography variant="body2">{pMemory}</Typography>
                                          </TableCell>
                                          <TableCell sx={{ py: 0.5 }}>
                                            <Typography variant="body2">{pThreads}</Typography>
                                          </TableCell>
                                          <TableCell sx={{ py: 0.5 }}>
                                            <Typography variant="body2">{pDbConn}</Typography>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {sorted.length > PAGE_SIZE && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <TablePagination
                component="div"
                count={sorted.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
